/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    BarChartAdvanced,
    Button,
    Panel,
    RangeSelectionPanel,
    Spinner,
    Text,
} from "vr-react-components";
import { EXPLORE_ROOM, HOME_ROOM, JOB_ROOM } from "../../rooms/RoomNameConstants";
import { getExploreRoomData } from "../../multiplayer/SocketConnection";
import { setNewGeneration, setTimeRage, setWorkstation, setCenterGraphCompare } from "../../store/slice/roomSlices/exploreRoomSlice";
import { getPlaceholderData } from "../../update/updateCenterGraph";
import { handleSetRotationGraphCompare, handleSetZoomGraphCompare } from "../../handlers/CenterChartHandlers";
import { handleSetLockControl } from "../../handlers/PrivateHandlers";

let placeholderData, isCenterGraphFilled = true;

const BarWidgetComparePanel = React.memo(({ stateName, width, height, device, manipulation, ...otherAttributes }) => {
    const centerGraphCompare = useSelector((state) => state.exploreroom.centerGraphCompare);
    const canModify = useSelector((state) => state.private.device.canModify);
    const centerGraph = useSelector((state) => state[stateName].data);
    const engines = useSelector((state) => state.user.engines);

    if (centerGraphCompare.data.hasOwnProperty('values')) {
        isCenterGraphFilled = centerGraphCompare.data.values.length
        if (!isCenterGraphFilled)
            placeholderData = getPlaceholderData({
                ...centerGraphCompare,
                data: { ...centerGraphCompare.data }
            });
    }

    const dispatch = useDispatch();

    switch (centerGraph.compare.prev) {
        case HOME_ROOM:
            return (
                <a-entity class="BarWidgetComparePanel" {...otherAttributes}>
                    <Button
                        disabled={!centerGraphCompare.isRequested}
                        aframeClasses={canModify ? "collidable" : ""}
                        width={width * 0.7}
                        height={height * 0.2}
                        position={'0 0.8 0'}
                        onClickHandler={() => {
                            dispatch(setNewGeneration(false));
                            dispatch(setCenterGraphCompare({}));
                        }}
                        type={Button.constants.primary}
                        text="Generate New Comparation"
                        textSizeDecimal={20}
                    />
                    {centerGraphCompare.isRequested ?
                        (centerGraphCompare.data && centerGraphCompare.data.xLabels ? (
                            <BarChartAdvanced
                                aframeClasses={canModify ? "collidable" : ""}
                                stateName={`centerGraphCompareCompare`}
                                width={width}
                                depth={width}
                                height={height * 1.5}
                                position={`0 ${-height / 2} 0`}
                                rotation="0 -45 0"
                                data={placeholderData || centerGraphCompare}
                                deviceData={device}
                                manipulationData={
                                    placeholderData
                                        ? {
                                            enabled: {
                                                VR: [],
                                                desktop: [],
                                            },
                                        }
                                        : manipulation
                                }
                                setZoom={handleSetZoomGraphCompare}
                                setRotation={handleSetRotationGraphCompare}
                                setLockControls={(callback) => {
                                    if (isCenterGraphFilled) handleSetLockControl(callback);
                                }}
                            />
                        ) : (
                            <Spinner radius={width / 6} position={`0 0 0`} />
                        )) : (
                            <Panel
                                id="right"
                                width={width}
                                height={height}
                                panel
                                aframeClasses={canModify ? "collidable" : ""}
                                rotation={`0 ${device.isMobile || device.areControllersConnected
                                    ? -45
                                    : 0
                                    } 0`}
                            >
                                <Text
                                    text="Compare with:"
                                    width={width * 0.7}
                                    sizeDecimal={16}
                                    position={`${-width * 0.45} ${height * 0.45} 0`}
                                    baseline="top"
                                />
                                <Text
                                    text={centerGraph.compare.compareTitle}
                                    width={width * 0.7}
                                    sizeDecimal={20}
                                    position={`${-width * 0.45} ${height * 0.3} 0`}
                                    baseline="top"
                                />
                                <RangeSelectionPanel
                                    title=""
                                    width={width}
                                    height={height}
                                    aframeClasses={canModify ? "collidable" : ""}
                                    button={{
                                        text: "See Graph",
                                        isButtonEnabled: centerGraphCompare.workstation !== "Select" &&
                                            centerGraphCompare.timeRange !== "Select",
                                        onClick: () => {
                                            dispatch(setNewGeneration(true))
                                            getExploreRoomData({
                                                workstation: centerGraphCompare.workstation,
                                                time: centerGraphCompare.timeRangeChoiches.indexOf(
                                                    centerGraphCompare.timeRange,
                                                ) + 1,
                                                engineList: engines.list.filter((engine) => engines.selected.includes(engine.name)),
                                                targetRoom: EXPLORE_ROOM
                                            });
                                        },
                                    }}
                                    textSize={20}
                                    from={{
                                        text: centerGraph.axis.z,
                                        current: centerGraphCompare.workstation,
                                        choices: centerGraph.zLabels,
                                        setCurrent: (newCurrent) => {
                                            dispatch(setWorkstation(newCurrent))
                                        },
                                    }}
                                    to={{
                                        text: "Time Range",
                                        current: centerGraphCompare.timeRange,
                                        choices: centerGraphCompare.timeRangeChoiches,
                                        setCurrent: (newCurrent) => {
                                            dispatch(setTimeRage(newCurrent))
                                        },
                                    }}
                                />
                            </Panel>
                        )}
                </a-entity>
            );
        case JOB_ROOM:
            return (
                <a-entity class="BarWidgetComparePanel" {...otherAttributes}>
                    <Panel
                        id="right"
                        width={width}
                        height={height}
                        panel
                        aframeClasses={canModify ? "collidable" : ""}
                        rotation={`0 ${device.isMobile || device.areControllersConnected
                            ? -45
                            : 0
                            } 0`}
                    >
                        <Text
                            text="Switch to graph:"
                            width={width}
                            sizeDecimal={16}
                            position={`${-width * 0.45} ${height * 0.45} 0`}
                            baseline="top"
                        />
                        <Text
                            text={centerGraph.compare.compareTitle}
                            width={width * 0.95}
                            sizeDecimal={20}
                            position={`${-width * 0.45} ${height * 0.3} 0`}
                            baseline="top"
                        />
                        <Button
                            disabled={false}
                            width={width * 0.7}
                            height={height * 0.2}
                            position={`${-width * 0.1} ${-height * 0.2} 0`}
                            aframeClasses={canModify ? "collidable" : ""}
                            onClickHandler={() => {

                            }}
                            type={Button.constants.primary}
                            text="Go to this explore room"
                            textSizeDecimal={20}
                        />
                    </Panel>
                </a-entity>
            );
        default:
            return null;
    }
},
);

export default BarWidgetComparePanel;
